import { BackendAbortController } from 'cadenza/api-client/abort-controller/backend-abort-controller';
import {
  determineErrorType,
  getIsochrones,
  getRoutingDirections
} from 'cadenza/api-client/routing-api';
import { Observable } from 'cadenza/utils/event-util';
import { CustomError } from 'cadenza/utils/custom-error';
import { debounceReturningPromise } from 'cadenza/utils/promise-utils';

export const STARTED_LOADING = 'startLoading';
export const FINISHED_LOADING = 'finishLoading';

const DEBOUNCE_DELAY = 500;

/**
 * This class is responsible to manage the requests for routes.
 *
 * The most important rule is that there can be only one active request at a time, so as soon as a
 * request is fired, if there is any pending request is must be canceled.
 *
 * The requests are sometimes, but not always, debounced. But when a debounced request is triggered,
 * any pending request must be canceled immediately, not after the debounce delay, otherwise we
 * might get the result of the ongoing request before the debounced request is really fired.
 *
 * The combination of single execution and debounce is why the existing SingleRequestExecutor was
 * not able to fulfill the requirement.
 *
 * The class also dispatches custom events:
 * - ROUTE_STARTED_LOADING: when it starts loading, i.e. when there is no pending request a request is fired
 * - ROUTE_FINISHED_LOADING: when there was a single pending request and it completes
 *
 * These events can be used to update loading indicators like spinners.
 */
export class RoutingRequestService extends Observable {

  #requestRouteDebounced;
  #abortController;
  #routeRequestPendingCounter = 0;

  constructor () {
    super();
    this.#requestRouteDebounced = debounceReturningPromise((routeRequest) => this._requestRouteInternal(routeRequest), DEBOUNCE_DELAY);
  }

  /**
   * Request the calculation of a route by the backend.
   * The error are handled and the combination of HTTP error code and error detail mapped to a
   * simple CustomError where the message speficies the type of error.
   *
   * @param {RouteRequest} routeRequest - The route request specifying the route to calculate
   * @param {boolean} [debounce=false] - If true, the request will be debounced
   * @return {Promise} A promise for a route.
   */
  requestRoute (routeRequest, debounce = false) {
    this.abortPendingRequest();

    return debounce
      ? this.#requestRouteDebounced(routeRequest)
      : this._requestRouteInternal(routeRequest);
  }

  /**
   * Request the calculation of isochrones by the backend.
   * The error are handled and the combination of HTTP error code and error detail mapped to a
   * simple CustomError where the message speficies the type of error.
   *
   * @param {IsochroneRequest} isochroneRequest - The isochrone request
   * @return {Promise} A promise for an isochrone result.
   */
  requestIsochrones (isochroneRequest) {
    this.abortPendingRequest();
    this._onRequestFired();

    this.#abortController = new BackendAbortController();
    this.#abortController.abortOnUnload = true;

    return getIsochrones(
      isochroneRequest,
      this.#abortController.signal
    )
      .catch(error => {
        throw transformError(error);
      })
      .finally(() => this._onRequestFinished());
  }

  /**
   * Abort the pending request of any.
   */
  abortPendingRequest () {
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }
  }

  _requestRouteInternal (routeRequest) {
    this._onRequestFired();
    this.#abortController = new BackendAbortController();
    this.#abortController.abortOnUnload = true;

    return getRoutingDirections(
      routeRequest,
      this.#abortController.signal
    )
      .catch(error => {
        throw transformError(error);
      })
      .finally(() => this._onRequestFinished());
  }

  _onRequestFired () {
    this.#routeRequestPendingCounter++;
    if (this.#routeRequestPendingCounter === 1) {
      this.dispatchEvent(new CustomEvent(STARTED_LOADING));
    }
  }

  _onRequestFinished () {
    this.#routeRequestPendingCounter = Math.max(0, this.#routeRequestPendingCounter - 1);
    if (this.#routeRequestPendingCounter === 0) {
      this.dispatchEvent(new CustomEvent(FINISHED_LOADING));
    }
  }

}

function transformError (error) {
  const errorType = determineErrorType(error);
  return new CustomError(errorType, error.detail);
}

